import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  fetchAllShorts,
  YOUTUBE_CHANNEL_URL,
  YOUTUBE_CHANNEL_HANDLE
} from "../config/youtube";
import { FaYoutube, FaArrowLeft, FaSearch, FaFire, FaPlay } from "react-icons/fa";
import "../styles/ShortsGallery.css";

const ShortsGallery = () => {
  const reelFeedRef = useRef(null);
  const [shorts, setShorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeModalIndex, setActiveModalIndex] = useState(null);

  const categories = [
    { id: "all", label: "All Shorts" },
    { id: "aarti", label: "🪔 Aarti & Chants" },
    { id: "pooja", label: "🌸 Special Poojas" },
    { id: "darshan", label: "✨ Divine Darshan" },
    { id: "annadanam", label: "🍲 Annadanam Seva" }
  ];

  const filteredShorts = React.useMemo(() => {
    return shorts.filter((item) => {
      const title = (item.title || "").toLowerCase();

      const isAnnadanam =
        item.category === "annadanam" ||
        title.includes("அன்னதான") ||
        title.includes("உணவே") ||
        title.includes("சாப்பாடு") ||
        title.includes("பிரம்மம்") ||
        title.includes("food");

      const isAarti =
        item.category === "aarti" ||
        title.includes("ஆரத்தி") ||
        title.includes("aarti");

      const isPooja =
        item.category === "pooja" ||
        title.includes("பூஜை") ||
        title.includes("ஹோமம்") ||
        title.includes("pooja") ||
        title.includes("homam");

      let matchCategory = selectedCategory === "all";
      if (selectedCategory === "annadanam") matchCategory = isAnnadanam;
      else if (selectedCategory === "aarti") matchCategory = isAarti;
      else if (selectedCategory === "pooja") matchCategory = isPooja;
      else if (selectedCategory === "darshan") matchCategory = item.category === "darshan" || (!isAnnadanam && !isAarti && !isPooja);

      const searchTerm = search.toLowerCase().trim();
      if (!searchTerm) return matchCategory;

      const category = (item.category || "").toLowerCase();
      const badge = (item.badge || "").toLowerCase();

      // Map common English search terms to Tamil equivalents
      let englishToTamil = "";
      if (searchTerm.includes("aarti") || searchTerm.includes("arti") || searchTerm.includes("arathi")) englishToTamil = "ஆரத்தி";
      else if (searchTerm.includes("pooja") || searchTerm.includes("puja")) englishToTamil = "பூஜை";
      else if (searchTerm.includes("homam") || searchTerm.includes("homo")) englishToTamil = "ஹோமம்";
      else if (searchTerm.includes("sai") || searchTerm.includes("baba")) englishToTamil = "சாய்";
      else if (searchTerm.includes("darshan") || searchTerm.includes("darsan")) englishToTamil = "தரிசனம்";
      else if (searchTerm.includes("anna") || searchTerm.includes("aana") || searchTerm.includes("dana") || searchTerm.includes("dhana") || searchTerm.includes("food")) englishToTamil = "அன்னதான";
      else if (searchTerm.includes("live")) englishToTamil = "live";

      const matchSearch =
        title.includes(searchTerm) ||
        category.includes(searchTerm) ||
        badge.includes(searchTerm) ||
        (englishToTamil && (title.includes(englishToTamil) || isAnnadanam));

      return matchCategory && matchSearch;
    });
  }, [shorts, selectedCategory, search]);

  /* ================= KEYBOARD & NATIVE VERTICAL REEL SCROLL ================= */
  useEffect(() => {
    if (activeModalIndex !== null && activeModalIndex >= filteredShorts.length) {
      setActiveModalIndex(null);
    }
  }, [filteredShorts.length, activeModalIndex]);

  useEffect(() => {
    if (activeModalIndex !== null && reelFeedRef.current) {
      setTimeout(() => {
        if (reelFeedRef.current) {
          const cardHeight = reelFeedRef.current.clientHeight || 580;
          reelFeedRef.current.scrollTop = activeModalIndex * cardHeight;
        }
      }, 50);
    }
  }, [activeModalIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeModalIndex === null) return;
      const len = filteredShorts.length || 1;
      if (e.key === "ArrowDown" || e.key === "PageDown") {
        e.preventDefault();
        const nextIdx = (activeModalIndex + 1) % len;
        setActiveModalIndex(nextIdx);
        if (reelFeedRef.current) {
          reelFeedRef.current.scrollTo({
            top: nextIdx * reelFeedRef.current.clientHeight,
            behavior: "smooth"
          });
        }
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        const prevIdx = (activeModalIndex - 1 + len) % len;
        setActiveModalIndex(prevIdx);
        if (reelFeedRef.current) {
          reelFeedRef.current.scrollTo({
            top: prevIdx * reelFeedRef.current.clientHeight,
            behavior: "smooth"
          });
        }
      } else if (e.key === "Escape") {
        setActiveModalIndex(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModalIndex]);

  useEffect(() => {
    const loadShorts = async () => {
      try {
        setLoading(true);
        const data = await fetchAllShorts();
        setShorts(data);
      } catch (err) {
        console.error("Error loading shorts gallery:", err);
      } finally {
        setLoading(false);
      }
    };

    loadShorts();
  }, []);

  return (
    <div className="shorts-gallery-page">
      {/* HEADER BANNER */}
      <div className="shorts-gallery-header">
        <Link to="/" className="back-link-btn">
          <FaArrowLeft /> Back to Home
        </Link>
        <div className="header-title-block">
          <a
            href={YOUTUBE_CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="channel-handle-tag clickable-channel-tag"
            title="Visit @srignanasaibabamandir YouTube Channel"
          >
            <FaYoutube className="yt-icon" /> {YOUTUBE_CHANNEL_HANDLE}
          </a>
          <h1>Divine Shorts & Reels Library</h1>
          <p>
            Experience short, powerful 30-second daily Aartis, special rituals,
            and divine darshans directly from Sri Gnana Sai Baba Mandir.
          </p>
        </div>

        <a
          href={YOUTUBE_CHANNEL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="subscribe-yt-btn"
        >
          <FaYoutube /> Subscribe on YouTube
        </a>
      </div>

      {/* CONTROLS: SEARCH & CATEGORY FILTER */}
      <div className="shorts-gallery-controls">
        <div className="shorts-search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search shorts by title or ritual..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="shorts-category-pills">
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`category-pill ${
                selectedCategory === cat.id ? "active" : ""
              }`}
              onClick={() => setSelectedCategory(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* SHORTS GRID CONTAINER */}
      {loading ? (
        <div className="shorts-gallery-loading">
          <div className="spinner-gold"></div>
          <p>Loading Divine Shorts Library...</p>
        </div>
      ) : filteredShorts.length === 0 ? (
        <div className="shorts-empty-state">
          <FaFire className="empty-icon" />
          <h3>No Shorts Found</h3>
          <p>Try searching for a different keyword or category.</p>
        </div>
      ) : (
        <div className="shorts-gallery-grid">
          {filteredShorts.map((short, idx) => (
            <div
              className="newsghuru-vertical-card"
              key={short.id || idx}
              onClick={() => setActiveModalIndex(idx)}
            >
              {/* TOP BADGE */}
              <div className="vertical-top-badge">{short.badge || "🪔 DIVINE"}</div>

              {/* VERTICAL HIGH-RES THUMBNAIL */}
              <div className="vertical-iframe-wrapper">
                <img
                  src={short.thumbnailUrl || `https://i.ytimg.com/vi/${short.videoId}/hqdefault.jpg`}
                  alt={short.title}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.src = "/images/banner.jpg";
                  }}
                />
              </div>

              {/* BOTTOM OVERLAY INFO */}
              <div className="vertical-bottom-overlay">
                <span className="play-reel-tag">
                  <FaPlay className="play-icon" /> Play Reel
                </span>
                <h3 className="reel-title-text">{short.title}</h3>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FULLSCREEN REEL MODAL FOR SELECTED SHORT (DOWNWARD SCROLL) */}
      {activeModalIndex !== null && filteredShorts && filteredShorts[activeModalIndex] && (
        <div
          className="shorts-modal-overlay"
          onClick={() => setActiveModalIndex(null)}
          onWheel={(e) => {
            const len = filteredShorts.length || 1;
            if (e.deltaY > 30) {
              setActiveModalIndex((prev) => (prev + 1) % len);
            } else if (e.deltaY < -30) {
              setActiveModalIndex((prev) => (prev - 1 + len) % len);
            }
          }}
        >
          <div
            className="shorts-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            {/* FLOATING CLOSE BUTTON DIRECTLY OVER VIDEO */}
            <button
              className="shorts-modal-close floating-close"
              onClick={() => setActiveModalIndex(null)}
              title="Close"
            >
              ✕
            </button>

            {/* NATIVE VERTICAL SNAP SCROLL FEED */}
            <div
              className="modal-vertical-snap-container"
              ref={reelFeedRef}
              onScroll={(e) => {
                const target = e.target;
                const index = Math.round(target.scrollTop / target.clientHeight);
                if (index !== activeModalIndex && index >= 0 && index < filteredShorts.length) {
                  setActiveModalIndex(index);
                }
              }}
            >
              {filteredShorts.map((item, idx) => (
                <div
                  className="shorts-snap-card"
                  key={item.id || idx}
                >
                  {idx === activeModalIndex ? (
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${item.videoId || (item.embedUrl?.split("embed/")[1]?.split("?")[0])}?autoplay=1&mute=0&controls=1&rel=0&playsinline=1&enablejsapi=1`}
                      title={item.title || `Sri Gnana Sai Baba Mandir Short Reel ${idx + 1}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  ) : (
                    <img
                      src={item.thumbnailUrl || `https://i.ytimg.com/vi/${item.videoId}/hqdefault.jpg`}
                      alt={item.title}
                      className="snap-card-thumb"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShortsGallery;
